import { APP_NAME, APP_FACEBOOK_APP_ID } from 'constants/index';
import React from 'react';
import { isMobile } from 'react-device-detect';
import {
	FacebookShareButton,
	FacebookMessengerShareButton,
	TwitterShareButton,
	WhatsappShareButton,
	LinkedinShareButton,
	EmailShareButton,
} from 'react-share';
import './styles.scss';
import useTranslate from 'hooks/useTranslate';

interface ExternalShareBtnProps {
	url: string;
	title?: string;
}

const ExternalShareBtn: React.FC<ExternalShareBtnProps> = ({ url, title = '' }) => {
	const { translate } = useTranslate();
	// Function to copy the link to the clipboard
	const copyToClipboard = () => {
		navigator.clipboard
			.writeText(url)
			.then(() => {
				alert('Link copied to clipboard!');
			})
			.catch((err) => {
				console.error('Failed to copy: ', err);
			});
	};

	return (
		<div className="flex gap-4">
			{/* Facebook */}
			<div className="group relative">
				<FacebookShareButton hashtag={title} url={url}>
					<i className="ri-facebook-fill text-blue-600 text-3xl hover:opacity-80"></i>
				</FacebookShareButton>
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.faceBook') })}
				</span>
			</div>

			{/* Messenger */}
			<div className="group relative">
				{isMobile ? (
					<a
						href={`fb-messenger://share?link=${encodeURIComponent(url)}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						<i className="ri-messenger-fill text-blue-500 text-3xl hover:opacity-80"></i>
					</a>
				) : (
					<FacebookMessengerShareButton url={url} appId={APP_FACEBOOK_APP_ID}>
						<i className="ri-messenger-fill text-blue-500 text-3xl hover:opacity-80"></i>
					</FacebookMessengerShareButton>
				)}
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.messenger') })}
				</span>
			</div>

			{/* Twitter */}
			<div className="group relative">
				<TwitterShareButton url={url} title={title}>
					<i className="ri-twitter-x-fill text-black text-3xl hover:opacity-80"></i>
				</TwitterShareButton>
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.twitter') })}
				</span>
			</div>

			{/* WhatsApp */}
			<div className="group relative">
				{isMobile ? (
					<a
						href={`whatsapp://send?text=${encodeURIComponent(`${title} ${url}`)}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						<i className="ri-whatsapp-fill text-green-500 text-3xl hover:opacity-80"></i>
					</a>
				) : (
					<WhatsappShareButton url={url} title={title}>
						<i className="ri-whatsapp-fill text-green-500 text-3xl hover:opacity-80"></i>
					</WhatsappShareButton>
				)}
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.whatsapp') })}
				</span>
			</div>

			{/* LinkedIn */}
			<div className="group relative">
				<LinkedinShareButton url={url} title={title} source={APP_NAME}>
					<i className="ri-linkedin-fill text-blue-700 text-3xl hover:opacity-80"></i>
				</LinkedinShareButton>
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.linkedIn') })}
				</span>
			</div>

			{/* Email */}
			<div className="group relative">
				<EmailShareButton subject={title} url={url}>
					<i className="ri-mail-fill text-blue-700 text-3xl hover:opacity-80"></i>
				</EmailShareButton>
				<span className="tooltip-2">
					{translate('Social.shareText', { plateform: translate('Social.email') })}
				</span>
			</div>

			{/* Copy Link */}
			<div className="group relative">
				<a onClick={copyToClipboard}>
					<i className="ri-link-m text-gray-500 text-3xl hover:opacity-80"></i>
				</a>
				<span className="tooltip-2">{translate('Social.copyLink')}</span>
			</div>
		</div>
	);
};

export default ExternalShareBtn;
